
/**
 * Module dependencies.
 */

import {
  Button,
  CopyToClipboard,
  Heading,
  Link,
  LocalImageSources,
  Text,
  typography,
  useImageSources
} from 'slyk-web-libs/design-system';

import { Container } from 'client/components/core/container';
import { DefaultProductItem } from 'client/components/core/products/default-product-item';
import { Icon } from 'react-components/media';
import { Task } from 'types/tasks';
import { TaskItem } from './task-item';
import { color, media } from 'react-components/styles';
import { getImageSources, resolveImageUrl } from 'client/core/utils/image';
import { ifProp, theme } from 'styled-tools';
import { range } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslate } from 'slyk-web-libs/i18n';
import React, { useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import linkIcon32 from 'client/assets/svg/32/link.svg';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import referralIcon from 'client/assets/svg/20/referral.svg';
import soundOffIcon from 'client/assets/svg/32/sound-off.svg';
import soundOnIcon from 'client/assets/svg/32/sound-on.svg';
import styled, { css } from 'styled-components';

/**
 * `NormalizedTask` type.
 */

type NormalizedTask = Task & {
  amountDefaultCurrency: string;
};

/**
 * `Props` type.
 */

type Props = {
  getSlykApp: () => void;
  task: NormalizedTask;
  tasks: Array<Task>;
};

/**
 * Max items.
 */

const maxItems = 5;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(6, 1fr);
  margin-top: 16px;
  position: relative;

  ${media.min('sm')`
    margin-top: 64px;
  `}

  ${media.min('ms')`
    gap: 56px 24px;
    grid-template-columns: repeat(8, 1fr);
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(12, 1fr);
  `}

  ${media.min('xl')`
    gap: 72px 32px;
    margin-bottom: 112px;
    margin-top: 104px;
    padding-left: 0;
    padding-right: 0;
  `}
`;

/**
 * `MediaWrapper` styled component.
 */

const MediaWrapper = styled.div`
  border: 1px solid ${color('gray200')};
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgba(25, 28, 30, 0.07);
  display: flex;
  grid-column: 1 / -1;
  grid-row: 1;
  height: max-content;
  position: relative;

  ${media.min('ms')`
    grid-column: 1 / span 3;
  `}

  ${media.min('lg')`
    grid-column: 1 / span 5;
  `}

  ${media.min('xl')`
    grid-column: 2 / span 4;
  `}
`;

/**
 * `VideoWrapper` styled component.
 */

const VideoWrapper = styled.div`
  display: contents;
`;

/**
 * `Video` styled component.
 */

const Video = styled.video`
  aspect-ratio: 3 / 4;
  border-radius: 6px;
  display: block;
  height: auto;
  object-fit: cover;
  width: 100%;
`;

/**
 * `SoundControlButton` styled component.
 */

const SoundControlButton = styled.button<{ isVisible: boolean }>`
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: ${ifProp('isVisible', 1, 0)};
  outline: none;
  padding: 0;
  position: absolute;
  transition: opacity ${theme('animations.defaultTransition')};
  width: 100%;
`;

/**
 * `MaskWrapper` styled component.
 */

const MaskWrapper = styled.div`
  background-color: ${color.transparentize('black', 0.1)};
  border-radius: 68px;
  height: 100px;
  position: absolute;
  width: 100px;

  ${media.min('xs')`
    height: 144px;
    width: 144px;
  `}
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div`
  align-items: center;
  background-color: ${color.transparentize('black', 0.5)};
  border-radius: 68px;
  display: flex;
  height: 56px;
  justify-content: center;
  width: 56px;

  ${media.min('xs')`
    height: 68px;
    width: 68px;
  `}
`;

/**
 * `Image` styled component.
 */

const Image = styled.img`
  aspect-ratio: 3 / 4;
  border-radius: 6px;
  display: block;
  height: auto;
  object-fit: cover;
  width: 100%;
`;

/*
 * `Content` styled component.
 */

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  grid-column: 1 / -1;
  grid-row: 2;

  ${media.min('ms')`
    gap: 24px;
    grid-column: 4 / -1;
    grid-row: 1;
    margin-bottom: 24px;
  `}

  ${media.min('lg')`
    grid-column: 6 / -1;
  `}

  ${media.min('xl')`
    grid-column: 6 / -2;
    margin-left: 32px;
  `}
`;

/**
 * `Card` styled component.
 */

const Card = styled.div`
  background-color: ${color('gray200')};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  padding: 16px;

  ${media.min('ms')`
    padding: 24px;
  `}
`;

/**
 * `TaskName` styled component.
 */

const TaskName = styled(Heading).attrs({ as: 'h1', level: 'h3' })`
  color: ${color('nightBlue500')};
  font-weight: 600;
  margin-bottom: 16px;
`;

/**
 * `TaskDescription` styled component.
 */

const TaskDescription = styled(ReactMarkdown)`
  ${typography.styles.textNormal}

  color: ${color('secondary')};
  margin: 8px 0 32px;
  position: relative;
  word-break: break-word;

  ${media.min('ms')`
    margin: 16px 0;
  `}

  p {
    margin: 0;
  }

  ul,
  ol {
    list-style: revert;
    margin: revert;
    padding: revert;
    text-align: start;
  }
`;

/**
 * `Tag` styled component.
 */

const Tag = styled.div`
  background-color: ${color('white')};
  border: 1px solid ${color('blue200')};
  border-radius: 8px;
  display: flex;
  gap: 6px;
  margin-bottom: 24px;
  padding: 3px 5px;
  width: max-content;
`;

/**
 * `TagText` styled component.
 */

const TagText = styled(Text).attrs({ as: 'span', size: 'small' })`
  color: ${color('blue500')};
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

/*
 * `MoreTasks` styled component.
 */

const MoreTasks = styled.div`
  grid-column: 1 / -1;
  grid-row: 3;

  ${media.min('ms')`
    grid-row: 2;
  `};

  ${media.min('xl')`
    grid-column: 2 / -2;
  `};
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 16px;

  ${media.min('ms')`
    margin-bottom: 24px;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h2', level: 'h4' })`
  color: ${color('nightBlue500')};
  font-weight: 600;
`;

/**
 * `ViewAll` styled component.
 */

const ViewAll = styled(Text).attrs({ as: 'span', size: 'small' })`
  font-weight: 700;
`;

/**
 * `List` styled component.
 */

const List = styled.ul`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: 40px;

  ${media.min('ms')`
    gap: 24px;
    grid-template-columns: repeat(4, 1fr);
    margin-bottom: 24px;
  `}

  ${media.min('xl')`
    gap: 32px;
    grid-template-columns: repeat(5, 1fr);
  `}
`;

/**
 * `ListItem` styled component.
 */

const ListItem = styled.li<{ removeLastChild: boolean}>`
  ${ifProp('removeLastChild', css`
    &:last-child {
      display: none;
    }
  `)};

  ${media.min('xl')`
    &:last-child {
      display: block;
    }
  `}
`;

/**
 * Export `TaskDetails` component.
 */

export function TaskDetails(props: Props): JSX.Element {
  const { getSlykApp, task, tasks } = props;
  const { translate } = useTranslate();
  const [isMuted, setMuted] = useState<boolean>(true);
  const [isSoundControlVisible, setSoundControlVisible] = useState<boolean>(false);
  const tasksLength = tasks?.length ?? 0;
  const defaultTasks = maxItems - tasksLength % maxItems;
  const showDefaultTasks = tasksLength < maxItems && defaultTasks !== maxItems;
  const { amount, amountDefaultCurrency, description, media, name } = task ?? {};
  const imageSources = useImageSources(resolveImageUrl(media), '128x');
  const noImageSources = getImageSources('no-image/product', 'webp') as LocalImageSources;
  const history = useHistory();
  const location = useLocation();
  const handleViewAll = useCallback(() => {
    return history.push({
      pathname: payspaceRoutes.home,
      state: {
        previousLocation: {
          ...location,
          state: { scrollToTasks: true }
        }
      }
    });
  }, [history, location]);

  return (
    <StyledContainer>
      <MediaWrapper>
        {media?.type === 'video' && !!media?.url ? (
          <VideoWrapper
            onMouseLeave={() => setSoundControlVisible(false)}
            onMouseOver={() => setSoundControlVisible(true)}
          >
            <Video
              autoPlay
              loop
              muted={isMuted}
              playsInline
            >
              <source src={media.url} />
            </Video>

            <SoundControlButton
              aria-label={isMuted ? translate('video.soundOff') : translate('video.soundOn')}
              isVisible={isSoundControlVisible}
              onClick={() => setMuted(!isMuted)}
            >
              <MaskWrapper />

              <IconWrapper>
                <Icon
                  aria-hidden
                  color={'white'}
                  icon={isMuted ? soundOffIcon : soundOnIcon}
                  size={'32px'}
                />
              </IconWrapper>
            </SoundControlButton>
          </VideoWrapper>
        ) : (
          <Image
            alt={''}
            src={imageSources?.defaultUrl ?? noImageSources.defaultUrl}
            srcSet={imageSources?.retinaUrl ?? noImageSources.retinaUrl}
          />
        )}
      </MediaWrapper>

      <Content>
        <Card>
          <TaskName>
            {name}
          </TaskName>

          <Tag>
            <Icon
              aria-hidden
              color={color('blue500')}
              icon={referralIcon}
              size={'20px'}
            />

            <TagText>
              {`${translate('taskDetails.earn')} ${amount}`}

              {!!amountDefaultCurrency && `/ ${amountDefaultCurrency}`}
            </TagText>
          </Tag>

          <Actions>
            <Button
              colorTheme={'success'}
              fullWidth
              onClick={getSlykApp}
              size={'large'}
            >
              {translate('earnRewards.startEarning')}
            </Button>

            <CopyToClipboard
              aria-label={translate('copyToClipboard.ariaLabel')}
              colorTheme={'gray'}
              copiedLabel={translate('copyToClipboard.copied')}
              icon={linkIcon32}
              iconSize={'large'}
              size={'large'}
              value={window.location.href}
            />
          </Actions>
        </Card>

        <TaskDescription>
          {description}
        </TaskDescription>
      </Content>

      {tasksLength > 0 && (
        <MoreTasks>
          <TitleWrapper>
            <Title>
              {translate('taskDetails.title')}
            </Title>

            <Link onClick={handleViewAll}>
              <ViewAll>
                {translate('common.labels.viewAll')}
              </ViewAll>
            </Link>
          </TitleWrapper>

          <List>
            {tasks.map((task, index) => {
              return (
                <ListItem
                  key={index}
                  removeLastChild={!showDefaultTasks}
                >
                  <TaskItem {...task} />
                </ListItem>
              );
            })}

            {showDefaultTasks && range(defaultTasks).map((_task, index) => {
              return (
                <ListItem
                  key={index}
                  removeLastChild={showDefaultTasks}
                >
                  <DefaultProductItem />
                </ListItem>
              );
            })}
          </List>
        </MoreTasks>
      )}
    </StyledContainer>
  );
}
